import { Settlement } from '../../models/Entities/Settlements/Settlement';
import { NewAgreementSettlement, NewPractitionerSettlement, NewProtocolSettlement, NewProviderSettlement } from '../../models/Entities/Settlements/NewSettlement';
import { buildUpdateCommand } from '../../components/Settlements/SettlementNameForm/SettlementNameForm.Logic';
import { ErrorType } from '../../models/HttpError';
import { AppState, AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/Settlements.Services';

// middelware
import * as SummariesMiddleware from './SettlementResumeMiddleware';
import * as DetailsMiddleware from './SettlementDetailsMiddleware';

// actions
import * as Actions from '../actions';
import { SettlementChangeStatus } from '../../models/Entities/Settlements/ChangeStatus/SettlementChangeStatus';

/** Ids de liquidación con un GET en curso. */
const settlementsInFlight = new Set<number>();

type KnownAction =
    | Actions.Settlements.Settlement_GetAll_Request_Action
    | Actions.Settlements.Settlement_GetAll_Receive_Action
    | Actions.Settlements.Settlement_GetAll_Fail_Action
    | Actions.Settlements.Settlement_UpdateAll_Request_Action
    | Actions.Settlements.Settlement_UpdateAll_Receive_Action
    | Actions.Settlements.Settlement_UpdateAll_Fail_Action
    | Actions.Settlements.Settlement_GetById_Request_Action
    | Actions.Settlements.Settlement_GetById_Receive_Action
    | Actions.Settlements.Settlement_GetById_Fail_Action
    | Actions.Settlements.Settlement_Add_Request_Action
    | Actions.Settlements.Settlement_Add_Receive_Action
    | Actions.Settlements.Settlement_Add_Fail_Action
    | Actions.Settlements.Settlement_Update_Request_Action
    | Actions.Settlements.Settlement_Update_Receive_Action
    | Actions.Settlements.Settlement_Update_Fail_Action
    | Actions.Settlements.Settlement_Delete_Request_Action
    | Actions.Settlements.Settlement_Delete_Receive_Action
    | Actions.Settlements.Settlement_Delete_Fail_Action
    | Actions.Settlements.Settlement_Recalculate_Request_Action
    | Actions.Settlements.Settlement_Recalculate_Receive_Action
    | Actions.Settlements.Settlement_Recalculate_Fail_Action
    | Actions.Settlements.Settlement_RefreshById_Request_Action
    | Actions.Settlements.Settlement_RefreshById_Receive_Action
    | Actions.Settlements.Settlement_RefreshById_Fail_Action
    | Actions.Settlements.Settlement_ChangeStatus_Request_Action
    | Actions.Settlements.Settlement_ChangeStatus_Receive_Action
    | Actions.Settlements.Settlement_ChangeStatus_Fail_Action
    | Actions.Settlements.Settlement_Reset_ChangeStatus_Flags_Action
    | Actions.Settlements.Settlement_GetByProtocolId_Request_Action
    | Actions.Settlements.Settlement_GetByProtocolId_Receive_Action
    | Actions.Settlements.Settlement_GetByProtocolId_Fail_Action;

    // local-only UI reset action
    // no service call; used to reset change-status flags for a specific settlement

export const actionCreators = {
    GetAllSettlements:
        (): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_GETALL_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            if (!state?.Settlement?.successLoadingAll) {
                dispatch({
                    type: 'SETTLEMENT_GETALL_REQUEST_ACTION'
                });

                Services.GetSettlements(token)
                    .then((settlements: Settlement[]) => {
                        dispatch({
                            type: 'SETTLEMENT_GETALL_RECEIVE_ACTION',
                            settlements: settlements
                        });
                    })
                    .catch((error: ErrorType) =>
                        dispatch({
                            type: 'SETTLEMENT_GETALL_FAIL_ACTION',
                            error: error
                        })
                    );
            } else {
                dispatch({
                    type: 'SETTLEMENT_UPDATEALL_REQUEST_ACTION'
                });

                Services.GetSettlements(token)
                    .then((settlements: Settlement[]) => {
                        dispatch({
                            type: 'SETTLEMENT_UPDATEALL_RECEIVE_ACTION',
                            settlements: settlements
                        });
                    })
                    .catch((error: ErrorType) =>
                        dispatch({
                            type: 'SETTLEMENT_UPDATEALL_FAIL_ACTION',
                            error: error
                        })
                    );
            }
    },
    GetSettlementById:
        (id: number): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_GETBYID_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            if(state.Settlement?.list?.find((settlement: Settlement) => settlement.id === id)) return;

            // Al abrir la página varios componentes piden la misma liquidación
            // antes de que llegue la primera respuesta: un solo pedido alcanza.
            if (settlementsInFlight.has(id)) return;
            settlementsInFlight.add(id);

            dispatch({
                type: 'SETTLEMENT_GETBYID_REQUEST_ACTION',
                settlementId: id
            });

            Services.GetSettlementById(id, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_GETBYID_RECEIVE_ACTION',
                    settlement: settlement
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_GETBYID_FAIL_ACTION',
                    error: error
                })
            )
            .finally(() => settlementsInFlight.delete(id));

    },
    AddProtocolSettlement:
        (newSettlement: NewProtocolSettlement): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_ADD_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_ADD_REQUEST_ACTION',
                newSettlement: newSettlement
            });

            Services.CreateProtocolSettlement(newSettlement, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_ADD_RECEIVE_ACTION',
                    settlement: settlement
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_ADD_FAIL_ACTION',
                    error: error
                })
            );

    },
    AddAgreementSettlement:
    (newSettlement: NewAgreementSettlement): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
        
        var state = getState();
        var token: string = '';

        if (state.oidc?.user) {
            token = state.oidc?.user?.access_token;
        } else {
            dispatch({
                type: 'SETTLEMENT_ADD_FAIL_ACTION',
                error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
            });
            return;
        }

        dispatch({
            type: 'SETTLEMENT_ADD_REQUEST_ACTION',
            newSettlement: newSettlement
        });

        Services.CreateAgreementSettlement(newSettlement, token)
        .then((settlement: Settlement) => {
            dispatch({
                type: 'SETTLEMENT_ADD_RECEIVE_ACTION',
                settlement: settlement
            });
        })
        .catch((error: ErrorType) =>
            dispatch({
                type: 'SETTLEMENT_ADD_FAIL_ACTION',
                error: error
            })
        );

    },
    AddPractitionerSettlement:
    (newSettlement: NewPractitionerSettlement): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
        
        var state = getState();
        var token: string = '';

        if (state.oidc?.user) {
            token = state.oidc?.user?.access_token;
        } else {
            dispatch({
                type: 'SETTLEMENT_ADD_FAIL_ACTION',
                error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
            });
            return;
        }

        dispatch({
            type: 'SETTLEMENT_ADD_REQUEST_ACTION',
            newSettlement: newSettlement
        });

        Services.CreatePractitionerSettlement(newSettlement, token)
        .then((settlement: Settlement) => {
            dispatch({
                type: 'SETTLEMENT_ADD_RECEIVE_ACTION',
                settlement: settlement
            });
        })
        .catch((error: ErrorType) =>
            dispatch({
                type: 'SETTLEMENT_ADD_FAIL_ACTION',
                error: error
            })
        );

    },
    /**
     * Alta de liquidación de proveedor (tipo 4, spec 010): mismas acciones SETTLEMENT_ADD_* que las demás altas y,
     * además, devuelve la promesa con la liquidación creada (para navegar al detalle) o rechaza con el ErrorType
     * (`ErrorCode: 409` + `data.settlementId` = liquidación de proveedor en curso).
     */
    AddProviderSettlement:
        (newSettlement: NewProviderSettlement) =>
        async (dispatch: (action: KnownAction) => void, getState: () => AppState): Promise<Settlement> => {
            const token = getState().oidc?.user?.access_token;
            if (!token) {
                const unauthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] };
                dispatch({ type: 'SETTLEMENT_ADD_FAIL_ACTION', error: unauthorized });
                throw unauthorized;
            }

            dispatch({ type: 'SETTLEMENT_ADD_REQUEST_ACTION', newSettlement });
            try {
                const settlement = await Services.CreateProviderSettlement(newSettlement, token);
                dispatch({ type: 'SETTLEMENT_ADD_RECEIVE_ACTION', settlement });
                return settlement;
            } catch (error: unknown) {
                dispatch({ type: 'SETTLEMENT_ADD_FAIL_ACTION', error: error as ErrorType });
                throw error;
            }
        },
    UpdateSettlement:
        (settlement: Settlement): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_UPDATE_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_UPDATE_REQUEST_ACTION',
                settlement: settlement
            });

            Services.UpdateSettlement(settlement, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_UPDATE_RECEIVE_ACTION',
                    settlement: settlement
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_UPDATE_FAIL_ACTION',
                    error: error
                })
            );

    },
    /** Cambia el nombre. El API pide el objeto completo, así que se arma desde la liquidación del store. */
    RenameSettlement:
        (settlementId: number, name: string): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {

            const state = getState();
            const current = state.Settlement?.list?.find((item: Settlement) => item.id === settlementId);

            if (!state.oidc?.user || !current) {
                dispatch({
                    type: 'SETTLEMENT_UPDATE_FAIL_ACTION',
                    error: { ErrorCode: !current ? 404 : 401, ErrorMessage: !current ? 'No se encontró la liquidación' : 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({ type: 'SETTLEMENT_UPDATE_REQUEST_ACTION', settlement: current });

            Services.UpdateSettlement(buildUpdateCommand(current, { name }), state.oidc.user.access_token)
            .then((settlement: Settlement) => {
                // Si el API devuelve el objeto, se usa; si no, alcanza con el nombre nuevo.
                dispatch({ type: 'SETTLEMENT_UPDATE_RECEIVE_ACTION', settlement: settlement && settlement.id ? settlement : { ...current, name } });
            })
            .catch((error: ErrorType) =>
                dispatch({ type: 'SETTLEMENT_UPDATE_FAIL_ACTION', error: error })
            );
    },
    RefreshSettlementById:
        (settlementId: number): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_REFRESHBYID_FAIL_ACTION',
                    settlementId: settlementId,
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_REFRESHBYID_REQUEST_ACTION',
                settlementId: settlementId
            });

            Services.GetSettlementById(settlementId, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_REFRESHBYID_RECEIVE_ACTION',
                    settlement: settlement
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_REFRESHBYID_FAIL_ACTION',
                    settlementId: settlementId,
                    error: error
                })
            );

    },
    ChangeSettlementStatus:
        (newSettlementStatus: SettlementChangeStatus): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_CHANGESTATUS_FAIL_ACTION',
                    settlementId: newSettlementStatus.settlementId,
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_CHANGESTATUS_REQUEST_ACTION',
                newStatus: newSettlementStatus,
                status: newSettlementStatus.status
            });

            Services.ChangeSettlementStatus(newSettlementStatus, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_CHANGESTATUS_RECEIVE_ACTION',
                    NewSettlementStatus: settlement.status,
                    settlementId: settlement.id
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_CHANGESTATUS_FAIL_ACTION',
                    settlementId: newSettlementStatus.settlementId,
                    error: error
                })
            );

    },
    ResetSettlementChangeStatusFlags:
        (settlementId: number): AppThunkAction<KnownAction> =>
        async (dispatch) => {
            dispatch({
                type: 'SETTLEMENT_RESET_CHANGESTATUS_FLAGS_ACTION',
                settlementId
            });
        },
    DeleteSettlementById:
        (settlementId: number): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_DELETE_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_DELETE_REQUEST_ACTION',
                settlementId: settlementId
            });

            Services.DeleteSettlement(settlementId, token)
            .then(() => {
                dispatch({
                    type: 'SETTLEMENT_DELETE_RECEIVE_ACTION',
                    settlementId: settlementId
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_DELETE_FAIL_ACTION',
                    error: error
                })
            );

    },
    RecalculateSettlementById:
        (settlementId: number): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {
            
            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_RECALCULATE_FAIL_ACTION',
                    settlementId: settlementId,
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_RECALCULATE_REQUEST_ACTION',
                settlementId: settlementId
            });

            Services.RecalculateSettlement(settlementId, token)
            .then((settlement: Settlement) => {
                dispatch({
                    type: 'SETTLEMENT_RECALCULATE_RECEIVE_ACTION',
                    settlement: settlement
                });

                SummariesMiddleware.actionCreators.GetAllSettlementResume(settlementId);
                DetailsMiddleware.actionCreators.GetAllSettlementDetails(settlementId)

            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_RECALCULATE_FAIL_ACTION',
                    settlementId: settlementId,
                    error: error
                })
            );
    },
    GetSettlementsByProtocolId:
        (protocolId: number): AppThunkAction<KnownAction> =>
        async (dispatch, getState) => {

            var state = getState();
            var token: string = '';

            if (state.oidc?.user) {
                token = state.oidc?.user?.access_token;
            } else {
                dispatch({
                    type: 'SETTLEMENT_GETBYPROTOCOLID_FAIL_ACTION',
                    error: { ErrorCode: 401, ErrorMessage: 'Unauthorized', Errors: [] }
                });
                return;
            }

            dispatch({
                type: 'SETTLEMENT_GETBYPROTOCOLID_REQUEST_ACTION',
                protocolId: protocolId
            });

            Services.GetSettlementsByProtocolId(protocolId, token)
            .then((settlements: Settlement[]) => {
                dispatch({
                    type: 'SETTLEMENT_GETBYPROTOCOLID_RECEIVE_ACTION',
                    settlements: settlements,
                    protocolId: protocolId
                });
            })
            .catch((error: ErrorType) =>
                dispatch({
                    type: 'SETTLEMENT_GETBYPROTOCOLID_FAIL_ACTION',
                    error: error
                })
            );
    }
};
